import * as SubframeCore from '@subframe/core';
import { useGetAccountTeam } from 'api/frontend';
import { TeamMember } from 'api/models';
import { Searchbar } from 'components/design-system/Searchbar';
import useUserAccountState from 'hooks/useUserAccountState';
import { SyntheticEvent, useEffect, useState, useRef } from 'react';
import { Button, DropdownMenu } from 'subframe/index';

interface OwnerSelectorProps {
  onConfirm?: (newOwner: string) => void;
  noLabel?: boolean;
}

function OwnerSelector({ onConfirm, noLabel }: OwnerSelectorProps) {
  const { account } = useUserAccountState();
  const { data: team, mutate: teamMutate } = useGetAccountTeam(
    'default',
    'default',
    {
      request: { headers: { Authorization: `Bearer ${account.token}` } },
    },
  );
  const [searchKeyword, setSearchKeyword] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [membersToShow, setMembersToShow] = useState<TeamMember[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdownAbove, setShowDropdownAbove] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const realUsers =
      team?.members?.filter((member) => member.user_email) || [];
    if (searchKeyword) {
      const filteredMembers = realUsers.filter((member) =>
        member.user_name?.toLowerCase().includes(searchKeyword.toLowerCase()),
      );
      setMembersToShow(filteredMembers);
    } else {
      setMembersToShow(realUsers || []);
    }
  }, [team?.members, searchKeyword]);

  const handleOnChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchKeyword(newValue);
    setIsOpen(true);
  };

  // Show members list dropdown above searchbar if no space below it on screen
  useEffect(() => {
    if (isOpen && searchbarRef.current) {
      const searchbarRect = searchbarRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - searchbarRect.bottom;
      const spaceAbove = searchbarRect.top;
      const dropdownHeight = 200; // max height of dropdown

      setShowDropdownAbove(
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow,
      );
    }
  }, [isOpen]);

  return (
    <div className="flex w-full items-center">
      {!noLabel && (
        <DropdownMenu.DropdownItem icon="FeatherUserCog2" disabled={true}>
          Change Owner
        </DropdownMenu.DropdownItem>
      )}
      <div className="relative" ref={dropdownRef}>
        <div
          ref={searchbarRef}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          <Searchbar
            onChange={handleOnChange}
            placeholderText={placeholder}
            browserAutocompleteOff={true}
            className="pl-0"
          />
        </div>
        {isOpen && (
          <div
            className={`absolute z-50 w-full rounded-md border border-neutral-border max-h-[200px] grow shrink-0 basis-0 overflow-auto [overflow-wrap:anywhere] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-black [&::-webkit-scrollbar-thumb]:rounded-[20px] [&::-webkit-scrollbar-thumb]:border-4 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(255,255,255,0.2)] [&::-webkit-scrollbar-thumb]:bg-clip-border ${
              showDropdownAbove ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            <div className="w-full flex flex-col gap-1 p-1 bg-black ">
              <Button
                variant="neutral-tertiary"
                type="button"
                className="w-full justify-start"
                onClick={() => {
                  setPlaceholder('Owner Not Required');
                  onConfirm?.('');
                  setIsOpen(false);
                }}
              >
                Owner Not Required
              </Button>
              {membersToShow.map((member) => (
                <Button
                  key={member.user_id}
                  variant="neutral-tertiary"
                  type="button"
                  className="w-full justify-start"
                  onClick={() => {
                    setPlaceholder(member.user_name);
                    onConfirm?.(member.user_id);
                    setIsOpen(false);
                  }}
                >
                  {member.user_name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OwnerSelector;
